<template>
  <div class="writing-grid" :class="{ 'is-compact': compact }">
    <!-- Placeholders while the request is in flight. They live here rather
         than in the two callers so the placeholder and the card it stands in
         for share one set of styles and occupy the same space. -->
    <template v-if="pending && !posts.length">
      <article
        v-for="n in compact ? 3 : 6"
        :key="`post-skeleton-${n}`"
        class="post-card is-skeleton"
        aria-hidden="true"
      >
        <div class="post-card__image-wrapper skeleton"/>
        <div class="post-card__content">
          <span class="sk-line sk-line--date skeleton"/>
          <span class="sk-line sk-line--title skeleton"/>
          <span class="sk-line skeleton"/>
          <span class="sk-line sk-line--short skeleton"/>
        </div>
      </article>
    </template>

    <!-- Every entry is a link out, which is what writing on this site is: the
         articles live on Medium, X and LinkedIn.

         A reader modal used to sit here for entries that carried their own
         text. Only the three placeholder articles compiled into the bundle
         ever did, and those are gone, so the modal could only be reached by a
         row saved with the link left blank, whereupon it opened a dialog with
         a title and an empty body: `admin_posts` has no `content` column, and
         the endpoint selects none. The admin now insists on a link, and a row
         without one stays an inert <article> rather than a card that promises
         something it cannot show. -->
    <component
      :is="post.url ? 'a' : 'article'"
      v-for="post in posts"
      :key="post.id"
      :href="post.url || undefined"
      :target="post.url ? '_blank' : undefined"
      :rel="post.url ? 'noopener noreferrer' : undefined"
      class="post-card"
    >
      <div class="post-card__image-wrapper">
        <img
          v-if="post.image"
          :src="post.image"
          :alt="post.title"
          class="post-card__image"
          loading="lazy"
          decoding="async"
        >
      </div>

      <div class="post-card__content">
        <span class="post-card__date">
          {{ formatDate(post.published_at || post.date) }}
          <template v-if="post.source"> · {{ post.source }}</template>
        </span>
        <h3 class="post-card__title">{{ post.title }}</h3>
        <!-- Inline Markdown only. The renderer escapes first, so this is
             safe for v-html; see utils/markdown.js. -->
        <p class="post-card__excerpt" v-html="renderInline(post.excerpt)"/>
      </div>
    </component>

  </div>
</template>

<script setup>
defineProps({
  posts: { type: Array, default: () => [] },
  // Whether the live request is still running. Only used to decide between a
  // placeholder and an empty state, which the caller cannot express by passing
  // an empty array.
  pending: { type: Boolean, default: false },
  // Inside the home track a card has to fit a 100vh panel alongside a heading.
  // On its own page there is no such ceiling.
  compact: { type: Boolean, default: false },
});

const formatDate = (dateStr) =>
  dateStr ? new Date(dateStr).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : '';
</script>

<style lang="scss" scoped>
.writing-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: clamp(0.75rem, 1vw + 1vh, 1.5rem);

  @media (max-width: 1000px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
}

.post-card.is-skeleton {
  cursor: default;

  &:hover {
    transform: none;
  }
}

.sk-line {
  display: block;
  height: 0.7rem;
  border-radius: 4px;
}

.sk-line--date {
  height: 0.6rem;
  width: 40%;
  margin-bottom: 0.15rem;
}

.sk-line--title {
  height: 0.95rem;
  width: 85%;
  margin-bottom: 0.15rem;
}

.sk-line--short {
  width: 55%;
}

.post-card__content .sk-line + .sk-line {
  margin-top: 0.35rem;
}

.post-card {
  // Half of these are anchors now, so the global `a:hover { underline }` would
  // rule every line inside them.
  color: inherit;
  text-decoration: none;

  &:hover { text-decoration: none; }

  display: flex;
  flex-direction: column;
  background: rgba(var(--muted-rgb), 0.04);
  border: 1px solid rgba(var(--border-rgb), 0.3);
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease-in-out, border-color 0.2s ease-in-out;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(var(--accent-rgb), 0.4);
  }
}

.post-card__image-wrapper {
  width: 100%;
  aspect-ratio: 16 / 10;
  overflow: hidden;
  flex-shrink: 0;
}

// Capped against viewport height as well as column width, so the heading plus
// a full row of cards always fits inside the 100vh panel rather than
// overflowing and getting clipped.
.writing-grid.is-compact .post-card__image-wrapper {
  max-height: 20vh;
}

.post-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.post-card__content {
  display: flex;
  flex-direction: column;
  gap: clamp(0.25rem, 0.6vh, 0.45rem);
  padding: clamp(0.7rem, 0.5vw + 0.9vh, 1.15rem);
}

.post-card__date {
  font-size: 0.72rem;
  color: var(--muted);
  letter-spacing: 0.4px;
}

.post-card__title {
  font-size: clamp(0.9rem, 0.5vw + 0.8vh, 1.05rem);
  font-weight: 700;
  color: var(--text);
  line-height: 1.35;
  margin: 0;
  // Line-clamped rather than cut off: these titles run long, and letting them
  // overflow their card was the visible "content cut off" problem.
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.post-card__excerpt {
  font-size: clamp(0.75rem, 0.3vw + 0.7vh, 0.85rem);
  color: var(--muted);
  line-height: 1.55;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

</style>
